import { useState } from 'react';
import axiosInstance from '../axiosInstance';
import { endpoints } from '../endPoints';
import { toast } from 'react-hot-toast';
import useToggle from '../../hooks/use-toggle';

const useRectorCommentService = () => {
  const [rectorComments, setRectorComments] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, toggleLoading] = useToggle();

  // Create new rector comment
  const createRectorComment = async (rectorCommentDto, onSuccess) => {
    try {
      const exists = await checkRectorCommentExists(rectorCommentDto.termId, rectorCommentDto.studentId);
      
      if (exists) {
        const response = await updateRectorCommentByTermStudent(
          rectorCommentDto.termId, 
          rectorCommentDto.studentId, 
          rectorCommentDto, 
          onSuccess
        );
        return response;
      } else {
        const response = await axiosInstance.post(endpoints.rectorComments.create, rectorCommentDto);
        toast.success(response.data.message || 'Rector comment created successfully');
        if (onSuccess) {
          onSuccess();
        }
        return response.data;
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error creating/updating rector comment');
      console.error('Error creating/updating rector comment:', error);
      throw error;
    }
  };

  // Create rector comment by term and student
  const createRectorCommentByTermStudent = async (rectorCommentDto, onSuccess) => {
    try {
      const response = await axiosInstance.post(endpoints.rectorComments.createByTermStudent, rectorCommentDto);
      toast.success(response.data.message || 'Rector comment created successfully');
      if (onSuccess) {
        onSuccess();
      }
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error creating rector comment');
      console.error('Error creating rector comment:', error);
      throw error;
    }
  };

  // Update rector comment
  const updateRectorComment = async (rectorCommentDto, onSuccess) => {
    try {
      toggleLoading();
      const response = await axiosInstance.put(endpoints.rectorComments.update, rectorCommentDto);
      toast.success(response.data.message || 'Rector comment updated successfully');
      if (onSuccess) {
        onSuccess();
      }
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating rector comment');
      console.error('Error updating rector comment:', error);
      throw error;
    } finally {
      toggleLoading();
    }
  };

  // Update rector comment by term and student
  const updateRectorCommentByTermStudent = async (termId, studentId, rectorCommentDto, onSuccess) => {
    try {
      toggleLoading();
      const response = await axiosInstance.put(
        `${endpoints.rectorComments.updateByTermStudent}/${termId}/${studentId}`,
        rectorCommentDto
      );
      toast.success(response.data.message || 'Rector comment updated successfully');
      if (onSuccess) {
        onSuccess();
      }
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating rector comment');
      console.error('Error updating rector comment:', error);
      throw error;
    } finally {
      toggleLoading();
    }
  };

  // Update rector comment with partial data
  const updatePartialRectorCommentByTermStudent = async (termId, studentId, partialDto, onSuccess) => {
    try {
      toggleLoading();
      const response = await axiosInstance.patch(
        `${endpoints.rectorComments.updatePartialByTermStudent}/${termId}/${studentId}`,
        partialDto
      );
      toast.success(response.data.message || 'Rector comment updated successfully');
      if (onSuccess) {
        onSuccess();
      }
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating rector comment');
      console.error('Error updating rector comment:', error);
      throw error;
    } finally {
      toggleLoading();
    }
  };

  // Get rector comment by term and student
  const getRectorCommentByTermStudent = async (termId, studentId) => {
    try {
      const response = await axiosInstance.get(
        `${endpoints.rectorComments.getByTermStudent}/${termId}/${studentId}`
      );
      return response.data.data;
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error(error.response?.data?.message || 'Error fetching rector comment');
      }
      console.error('Error fetching rector comment:', error);
      throw error;
    }
  };

  // Get all rector comments for a student
  const getRectorCommentsByStudent = async (studentId) => {
    try {
      toggleLoading();
      const response = await axiosInstance.get(`${endpoints.rectorComments.getByStudent}/${studentId}`);
      return response.data.data || [];
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching rector comments');
      console.error('Error fetching rector comments:', error);
      throw error;
    } finally {
      toggleLoading();
    }
  };

  // Get all rector comments for a term
  const getRectorCommentsByTerm = async (termId) => {
    try {
      toggleLoading();
      const response = await axiosInstance.get(`${endpoints.rectorComments.getByTerm}/${termId}`);
      return response.data.data || [];
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fetching rector comments');
      console.error('Error fetching rector comments:', error);
      throw error;
    } finally {
      toggleLoading();
    }
  };

  // Check if rector comment exists
  const checkRectorCommentExists = async (termId, studentId) => {
    try {
      const response = await axiosInstance.get(
        `${endpoints.rectorComments.exists}/${termId}/${studentId}`
      );
      return response.data.data || false;
    } catch (error) {
      if (error.response?.status === 404) {
        return false;
      }
      console.error('Error checking rector comment existence:', error);
      return false;
    }
  };

  // Delete rector comment
  const deleteRectorCommentById = async (rectorCommentId) => {
    try {
      toggleLoading();
      const response = await axiosInstance.delete(`${endpoints.rectorComments.delete}${rectorCommentId}`);
      toast.success(response.data.message || 'Rector comment deleted successfully');
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting rector comment');
      console.error('Error deleting rector comment:', error);
      throw error;
    } finally {
      toggleLoading();
    }
  };

  return {
    rectorComments,
    totalRecords,
    isLoading,
    createRectorComment,
    createRectorCommentByTermStudent,
    updateRectorComment,
    updateRectorCommentByTermStudent,
    updatePartialRectorCommentByTermStudent,
    getRectorCommentByTermStudent,
    getRectorCommentsByStudent,
    getRectorCommentsByTerm,
    checkRectorCommentExists,
    deleteRectorCommentById,
  };
};

export default useRectorCommentService;

